import Cookies from 'js-cookie';
import axios from 'axios';
import { API_URL, getCurrentTeamId } from '../Utils/utils';
import Session from '../Utils/Session';

// Key names for tokens
const AUTH_TOKEN_KEY = 'AUTH-TOKEN';
const SESSION_TOKEN_KEY = 'AUTH-TOKEN';
const CSRF_TOKEN_KEY = 'XSRF-TOKEN';
export const ACCOUNT_SUSPENDED_REDIRECT_KEY = 'account-suspended-redirect';
const AUTH_GENERATION_KEY = 'auth-generation';

let csrfTokenLoaded = false;
let csrfReadyGeneration = null;
let csrfBootstrapPromise = null;
let csrfBootstrapGeneration = null;
let csrfBootstrapEpoch = 0;
// --- AUTH TOKEN LOGIC ---

// Get the auth token from either session storage or cookies
export const getAuthToken = () => {
  // Try to get the token from session storage
  let token = sessionStorage.getItem(SESSION_TOKEN_KEY);

  if (!token) {
    token = localStorage.getItem(SESSION_TOKEN_KEY);
  }

  // If it's not found in session storage, get it from the cookie
  if (!token) {
    token = Cookies.get(AUTH_TOKEN_KEY);
  }

  return token;
};

// Set the auth token in both session storage and cookies
export const setAuthToken = (token) => {
  if (getAuthToken() !== token) advanceAuthGeneration();
  // Store in session storage
  sessionStorage.setItem(SESSION_TOKEN_KEY, token);

  localStorage.setItem(SESSION_TOKEN_KEY, token);

  // Store in cookies (7-day expiration, can be adjusted)
  Cookies.set(AUTH_TOKEN_KEY, token, { expires: 7 });
};

// Remove the auth token from both session storage and cookies (for logout)
export const removeAuthToken = () => {
  advanceAuthGeneration();
  // Remove from session storage
  sessionStorage.removeItem(SESSION_TOKEN_KEY);

  localStorage.removeItem(SESSION_TOKEN_KEY);

  // Remove from cookies
  Cookies.remove(AUTH_TOKEN_KEY);
};

const advanceAuthGeneration = () => {
  sessionStorage.setItem(
    AUTH_GENERATION_KEY,
    String(Number(sessionStorage.getItem(AUTH_GENERATION_KEY) || 0) + 1),
  );
};

export const redirectSuspendedAccount = () => {
  removeAuthToken();
  Session.removeSessionVar("currentTeam");
  sessionStorage.removeItem("currentTeam");
  localStorage.removeItem("currentTeam");
  getCurrentTeamId();
  sessionStorage.setItem(ACCOUNT_SUSPENDED_REDIRECT_KEY, "1");

  if (!window.location.pathname.startsWith("/sign-in")) {
    window.location.assign("/sign-in");
  }
};

export const getAuthGeneration = () =>
  String(Number(sessionStorage.getItem(AUTH_GENERATION_KEY) || 0));

// --- CSRF TOKEN LOGIC ---

// Get the CSRF token (ensure it is loaded if needed)
export const getCsrfToken = async () => {
  const isLocalOnline = process.env.REACT_APP_ENV === 'local-online';

  if (isLocalOnline) {
    const authToken = getAuthToken();
    if (!authToken) {
      throw new Error("No authentication token found. Please login.");
    }
    return; // No CSRF needed in local-online
  }

  const generation = getAuthGeneration();
  if (csrfTokenLoaded && csrfReadyGeneration === generation) return;
  if (csrfBootstrapPromise && csrfBootstrapGeneration === generation) {
    return csrfBootstrapPromise;
  }

  const epoch = ++csrfBootstrapEpoch;
  csrfBootstrapGeneration = generation;
  let pending;
  pending = Promise.resolve()
    .then(() => axios.get(`${API_URL}/sanctum/csrf-cookie`, { withCredentials: true, timeout: 15000 }))
    .then(() => {
      if (epoch !== csrfBootstrapEpoch || generation !== getAuthGeneration()) {
        throw new Error('Your session changed. Please try again.');
      }
      csrfTokenLoaded = true;
      csrfReadyGeneration = generation;
    })
    .catch((error) => {
      if (epoch === csrfBootstrapEpoch) {
        csrfTokenLoaded = false;
        csrfReadyGeneration = null;
      }
      throw error;
    })
    .finally(() => {
      if (csrfBootstrapPromise === pending) {
        csrfBootstrapPromise = null;
        csrfBootstrapGeneration = null;
      }
    });
  csrfBootstrapPromise = pending;
  return pending;
};

// Reset the CSRF token by marking it as unloaded
export const resetCsrfToken = async () => {
  csrfTokenLoaded = false;
  csrfReadyGeneration = null;
  await getCsrfToken();
};

// Optional: Function to refresh the auth token if using refresh tokens
export const refreshAuthToken = async () => {
  try {
    const response = await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
    if (response.data && response.data.authToken) {
      setAuthToken(response.data.authToken);
    }
  } catch (error) {
    console.error("Failed to refresh auth token", error);
  }
};
